<template>
    <div class="dashboard tk-detail-page">
        <main class="tk-detail-content">
            <q-banner v-if="error" rounded class="bg-red-1 text-negative q-ma-md">
                {{ error }}
            </q-banner>

            <template v-if="group">
                <header class="tk-topbar">
                    <div class="tk-topbar__left">
                        <q-btn flat round dense icon="arrow_back" @click="$router.push({ name: 'tkGroups' })">
                            <q-tooltip class="text-body2">Back to Groups</q-tooltip>
                        </q-btn>
                        <nav class="tk-breadcrumb" aria-label="Breadcrumb">
                            <button type="button" @click="$router.push({ name: 'tkGroups' })">Groups</button>
                            <q-icon name="chevron_right" size="18px" />
                            <span>{{ group.group_name }}</span>
                        </nav>
                    </div>
                </header>

                <section class="tk-workspace-header">
                    <div class="tk-workspace-header__identity">
                        <q-avatar class="tk-workspace-header__avatar" size="50px">
                            <q-icon name="o_groups" size="26px" />
                        </q-avatar>
                        <div>
                            <h1>{{ group.group_name }}</h1>
                            <div class="tk-workspace-header__badges">
                                <q-badge
                                    rounded
                                    outline
                                    :color="group.status_code === 'active' ? 'positive' : 'grey-7'"
                                    :label="group.status_code === 'active' ? 'Active' : 'Concluded'"
                                />
                                <q-badge rounded outline color="primary" :label="group.service_name || '-'" />
                            </div>
                        </div>
                    </div>

                    <dl class="tk-workspace-header__facts">
                        <div v-for="fact in headerFacts" :key="fact.key">
                            <dt>{{ fact.label }}</dt>
                            <dd>{{ fact.value }}</dd>
                        </div>
                    </dl>
                </section>

                <section class="tk-page-grid">
                    <section class="tk-main-column">
                        <section class="tk-card">
                            <div class="tk-card-header">
                                <h2>Group Members</h2>
                                <q-btn flat dense no-caps color="primary" icon="o_edit" label="Edit" @click="openMembersDialog" />
                            </div>
                            <q-list v-if="activeMembers.length" separator>
                                <q-item v-for="member in activeMembers" :key="member.id">
                                    <q-item-section avatar>
                                        <q-avatar color="primary" text-color="white" size="36px">
                                            <q-icon :name="member.tk_service_engagement_id ? 'o_person' : 'o_badge'" />
                                        </q-avatar>
                                    </q-item-section>
                                    <q-item-section>
                                        <q-item-label>{{ memberName(member) }}</q-item-label>
                                        <q-item-label caption>
                                            {{ member.tk_service_engagement_id ? 'Client Engagement' : 'Reporting-only member' }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                            </q-list>
                            <div v-else class="text-grey-7">No group members have been added.</div>
                        </section>

                        <section class="tk-card">
                            <div class="tk-card-header">
                                <h2>Notes</h2>
                            </div>
                            <p v-if="group.notes" class="tk-group-notes text-body2 text-grey-8">{{ group.notes }}</p>
                            <div v-else class="text-grey-7">No notes have been added.</div>
                        </section>

                        <section class="tk-card">
                            <div class="tk-card-header">
                                <h2>Sessions</h2>
                                <q-btn
                                    flat
                                    dense
                                    no-caps
                                    color="primary"
                                    icon="o_note_add"
                                    label="Add Session"
                                    @click="openNewSession"
                                />
                            </div>

                            <q-table
                                :rows="group.sessions"
                                :columns="sessionColumns"
                                row-key="id"
                                flat
                                dense
                                :pagination="{ rowsPerPage: 10 }"
                                :rows-per-page-options="[10, 20, 50]"
                                class="client-table tk-sessions-table"
                                @row-click="openSession"
                            >
                                <template v-slot:body-cell-facilitators="props">
                                    <q-td :props="props">{{ facilitatorNames(props.row) }}</q-td>
                                </template>
                                <template v-slot:body-cell-recorded="props">
                                    <q-td :props="props">
                                        {{ props.row.attended_member_ids.length }}
                                    </q-td>
                                </template>
                                <template v-slot:body-cell-action="props">
                                    <q-td :props="props">
                                        <q-btn flat round dense color="primary" icon="o_edit" aria-label="Edit session" @click.stop="openSession(null, props.row)" />
                                    </q-td>
                                </template>
                                <template v-slot:no-data>
                                    <div class="full-width row flex-center text-grey-7 q-pa-md">
                                        No sessions have been added.
                                    </div>
                                </template>
                            </q-table>
                        </section>
                    </section>

                    <aside class="tk-aside-column">
                        <section class="tk-side-card">
                            <div class="tk-card-header tk-card-header--compact">
                                <h2>Group Details</h2>
                                <q-btn flat dense no-caps color="primary" icon="o_edit" label="Edit" @click="showGroupDialog = true" />
                            </div>
                            <div class="tk-engagement-detail-row">
                                <span>Group Type</span>
                                <strong>{{ group.service_name || '-' }}</strong>
                            </div>
                            <div class="tk-engagement-detail-row">
                                <span>Group Name</span>
                                <strong>{{ group.group_name }}</strong>
                            </div>
                            <div class="tk-engagement-detail-row">
                                <span>Coordinator</span>
                                <strong>{{ coordinatorName }}</strong>
                            </div>
                            <div class="tk-engagement-detail-row">
                                <span>Status</span>
                                <strong>
                                    <q-badge
                                        rounded
                                        outline
                                        :color="group.status_code === 'active' ? 'positive' : 'grey-7'"
                                        :label="group.status_code === 'active' ? 'Active' : 'Concluded'"
                                    />
                                </strong>
                            </div>
                        </section>
                    </aside>
                </section>

                <TkGroupSessionDialog
                    v-model="showSessionDialog"
                    :session="selectedSession"
                    :staff-options="staffOptions"
                    :member-options="memberOptions"
                    :default-facilitator-ids="defaultFacilitatorIds"
                    :saving="savingSession"
                    @save="saveSession"
                />

                <q-dialog v-model="showGroupDialog">
                    <q-card class="tk-dialog-card" style="width: 900px; max-width: 96vw;">
                        <q-card-section class="tk-dialog-header">
                            <h2>Group Details</h2>
                            <q-btn flat round dense icon="close" v-close-popup>
                                <q-tooltip class="text-body2">Close</q-tooltip>
                            </q-btn>
                        </q-card-section>
                        <q-form ref="groupForm" @submit.prevent="saveGroup">
                            <q-card-section>
                                <div class="row q-col-gutter-md">
                                    <div v-if="group.group_type_allows_custom_name" class="col-12 col-md-4 tk-field">
                                        <p class="q-mb-xs"><strong>Group Name</strong></p>
                                        <q-input
                                            v-model="details.customName"
                                            outlined
                                            dense
                                            maxlength="255"
                                            lazy-rules="ondemand"
                                            hide-bottom-space
                                            :rules="[value => !!String(value || '').trim() || 'Group Name is required.']"
                                        />
                                    </div>
                                    <div class="col-12 col-md-4 tk-field">
                                        <p class="q-mb-xs"><strong>Coordinator</strong></p>
                                        <q-select
                                            v-model="details.coordinatorId"
                                            :options="staffOptions"
                                            outlined
                                            dense
                                            emit-value
                                            map-options
                                            lazy-rules="ondemand"
                                            hide-bottom-space
                                            :rules="[value => !!value || 'Coordinator is required.']"
                                        />
                                    </div>
                                    <div class="col-12 col-md-4 tk-field">
                                        <p class="q-mb-xs"><strong>Status</strong></p>
                                        <q-select
                                            v-model="details.statusCode"
                                            :options="groupStatusOptions"
                                            outlined
                                            dense
                                            emit-value
                                            map-options
                                            lazy-rules="ondemand"
                                            hide-bottom-space
                                            :rules="[value => !!value || 'Status is required.']"
                                        />
                                    </div>
                                    <div class="col-12 tk-field">
                                        <p class="q-mb-xs"><strong>Notes</strong></p>
                                        <q-input v-model="details.notes" outlined dense type="textarea" rows="8" />
                                    </div>
                                </div>
                            </q-card-section>
                            <q-card-actions align="right">
                                <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                                <q-btn unelevated no-caps color="primary" icon="save" label="Save" type="submit" :loading="savingGroup" />
                            </q-card-actions>
                        </q-form>
                    </q-card>
                </q-dialog>

                <q-dialog v-model="showMembersDialog">
                    <q-card class="tk-dialog-card" style="width: 900px; max-width: 96vw;">
                        <q-card-section class="tk-dialog-header">
                            <h2>Group Members</h2>
                            <q-btn flat round dense icon="close" v-close-popup>
                                <q-tooltip class="text-body2">Close</q-tooltip>
                            </q-btn>
                        </q-card-section>
                        <q-form @submit.prevent="saveMembers">
                            <q-card-section>
                                <div class="row q-col-gutter-md">
                                    <div class="col-12 tk-field">
                                        <p class="q-mb-xs"><strong>Linked Client Engagements</strong></p>
                                        <q-select
                                            v-model="memberForm.engagementIds"
                                            :options="filteredEngagementOptions"
                                            outlined
                                            dense
                                            emit-value
                                            map-options
                                            multiple
                                            use-chips
                                            use-input
                                            input-debounce="0"
                                            hint="Only Client Engagements for this group’s Group Type are shown."
                                            @filter="filterEngagements"
                                        />
                                    </div>
                                    <div class="col-12 tk-field">
                                        <div class="row items-center q-mb-sm">
                                            <div>
                                                <p class="q-mb-xs"><strong>Reporting-only Member Names</strong></p>
                                                <div class="text-caption text-grey-7">No person or Client Engagement record will be created.</div>
                                            </div>
                                            <q-space />
                                            <q-btn flat dense no-caps color="primary" icon="add" label="Add Name" @click="addReportingMember" />
                                        </div>
                                        <div v-for="(member, index) in memberForm.reportingMembers" :key="member.key" class="row items-start q-col-gutter-sm q-mb-sm">
                                            <div class="col">
                                                <q-input v-model="member.name" outlined dense :label="`Member ${index + 1}`" maxlength="255" />
                                            </div>
                                            <div class="col-auto">
                                                <q-btn flat round dense color="negative" icon="delete_outline" :aria-label="`Remove member ${index + 1}`" @click="removeReportingMember(index)" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </q-card-section>
                            <q-card-actions align="right">
                                <q-btn flat no-caps color="negative" label="Cancel" v-close-popup />
                                <q-btn unelevated no-caps color="primary" icon="save" label="Save Members" type="submit" :loading="savingMembers" />
                            </q-card-actions>
                        </q-form>
                    </q-card>
                </q-dialog>
            </template>

            <q-inner-loading :showing="loading" />
        </main>
    </div>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import TkGroupSessionDialog from "@/components/TkClients/TkGroupSessionDialog.vue";
import {
    engagementPersonName,
    formatDate,
    formatMinutes,
    staffOption,
} from "@/services/util/tkClients";

const errorMessage = (error, fallbackMessage) =>
    error.response?.data?.error || error.response?.statusText || fallbackMessage;

const groupMemberName = (member) => member.reporting_name || engagementPersonName(member);

export default {
    name: "TkGroupDetail",
    components: { TkGroupSessionDialog },
    data() {
        return {
            loading: false,
            savingGroup: false,
            savingMembers: false,
            savingSession: false,
            error: "",
            group: null,
            staff: [],
            engagements: [],
            filteredEngagementOptions: [],
            details: { customName: "", coordinatorId: null, notes: "", statusCode: "active" },
            memberKey: 0,
            memberForm: { engagementIds: [], reportingMembers: [] },
            groupStatusOptions: [
                { label: "Active", value: "active" },
                { label: "Concluded", value: "concluded" },
            ],
            selectedSession: null,
            showGroupDialog: false,
            showMembersDialog: false,
            showSessionDialog: false,
            sessionColumns: [
                { name: "date", label: "Session date", field: row => formatDate(row.session_date), align: "left", sortable: true },
                { name: "time", label: "Time spent", field: row => formatMinutes(row.time_spent_minutes), align: "left" },
                { name: "facilitators", label: "Facilitators", field: "facilitators", align: "left" },
                { name: "total", label: "Total attendees", field: "total_attendee_count", align: "right", sortable: true },
                { name: "recorded", label: "Attended members", field: "recorded", align: "right" },
                { name: "action", label: "", field: "action", align: "right" },
            ],
        };
    },
    computed: {
        coordinatorName() {
            return this.group?.coordinator_name || this.group?.coordinator_email || "-";
        },
        latestSessionDate() {
            return formatDate(this.group?.sessions?.[0]?.session_date);
        },
        headerFacts() {
            return [
                { key: "sessions", label: "Sessions", value: this.group.sessions.length },
                { key: "members", label: "Active members", value: this.activeMembers.length },
                { key: "last-session", label: "Last session", value: this.latestSessionDate },
                { key: "coordinator", label: "Coordinator", value: this.coordinatorName },
                { key: "created", label: "Created", value: formatDate(this.group.created_at) },
                { key: "last-updated", label: "Last updated", value: formatDate(this.group.updated_at) },
            ];
        },
        activeMembers() {
            return (this.group?.members || []).filter(member => member.is_active);
        },
        memberOptions() {
            return (this.group?.members || []).map(member => ({
                label: groupMemberName(member),
                value: member.id,
                isActive: !!member.is_active,
                typeLabel: member.tk_service_engagement_id
                    ? "Client Engagement"
                    : "Reporting-only member",
            }));
        },
        staffOptions() {
            return this.staff.map(staffOption);
        },
        engagementOptions() {
            const linkedEngagements = (this.group?.members || [])
                .filter(member => member.tk_service_engagement_id)
                .map(member => ({ ...member, id: member.tk_service_engagement_id }));
            const engagementsById = new Map(
                [...this.engagements, ...linkedEngagements].map(engagement => [String(engagement.id), engagement])
            );
            return [...engagementsById.values()].map(engagement => ({
                label: `${engagementPersonName(engagement)} · ${engagement.status_name || engagement.status_code}`,
                value: engagement.id,
            }));
        },
        currentStaffId() {
            const email = String(token.getEmail() || "").toLowerCase();
            return this.staff.find(member => String(member.email || "").toLowerCase() === email)?.id || null;
        },
        defaultFacilitatorIds() {
            const selectableStaffIds = new Set(this.staffOptions.map(option => option.value));
            const previousFacilitators = this.group?.sessions?.[0]?.facilitators
                ?.map(facilitator => facilitator.staff_id)
                .filter(staffId => selectableStaffIds.has(staffId)) || [];
            return previousFacilitators.length
                ? previousFacilitators
                : this.currentStaffId
                    ? [this.currentStaffId]
                    : [];
        },
    },
    async mounted() {
        await this.load();
    },
    methods: {
        formatDate,
        applyGroup(group) {
            this.group = group;
            this.details = {
                customName: group.custom_name || "",
                coordinatorId: group.coordinator_id || null,
                notes: group.notes || "",
                statusCode: group.status_code || "active",
            };
        },
        async load() {
            this.loading = true;
            this.error = "";
            try {
                const groupResponse = await api.groupEngagements.getGroup(this.$route.params.groupId);
                this.applyGroup(groupResponse.data);
                const [staffResponse, engagementResponse] = await Promise.all([
                    api.users.getAppStaffList(),
                    api.tkPeople.listServiceEngagements({ service: this.group.service_code, limit: 200 }),
                ]);
                this.staff = staffResponse.data || [];
                this.engagements = engagementResponse.data || [];
                this.filteredEngagementOptions = [...this.engagementOptions];
            } catch (error) {
                this.error = errorMessage(error, "Failed to load group details");
            } finally {
                this.loading = false;
            }
        },
        facilitatorNames(session) {
            return session.facilitators
                .map(facilitator => facilitator.staff_name || facilitator.staff_email)
                .filter(Boolean)
                .join(", ") || "-";
        },
        memberName(member) {
            return groupMemberName(member);
        },
        openMembersDialog() {
            const activeMembers = this.activeMembers;
            this.memberForm = {
                engagementIds: activeMembers
                    .filter(member => member.tk_service_engagement_id)
                    .map(member => member.tk_service_engagement_id),
                reportingMembers: activeMembers
                    .filter(member => member.reporting_name)
                    .map(member => ({ key: ++this.memberKey, name: member.reporting_name })),
            };
            this.filteredEngagementOptions = [...this.engagementOptions];
            this.showMembersDialog = true;
        },
        addReportingMember() {
            this.memberForm.reportingMembers = [
                ...this.memberForm.reportingMembers,
                { key: ++this.memberKey, name: "" },
            ];
        },
        removeReportingMember(index) {
            this.memberForm.reportingMembers = this.memberForm.reportingMembers
                .filter((member, memberIndex) => memberIndex !== index);
        },
        filterEngagements(value, update) {
            update(() => {
                const query = String(value || "").trim().toLowerCase();
                this.filteredEngagementOptions = query
                    ? this.engagementOptions.filter(option => option.label.toLowerCase().includes(query))
                    : [...this.engagementOptions];
            });
        },
        async saveMembers() {
            this.savingMembers = true;
            try {
                const response = await api.groupEngagements.updateGroupMembers(this.group.id, {
                    engagement_ids: [...this.memberForm.engagementIds],
                    reporting_names: this.memberForm.reportingMembers
                        .map(member => member.name.trim())
                        .filter(Boolean),
                });
                this.applyGroup(response.data);
                this.showMembersDialog = false;
                notify.primary("Group members saved", "top");
            } catch (error) {
                notify.error(errorMessage(error, "Failed to save group members"), "top");
            } finally {
                this.savingMembers = false;
            }
        },
        async saveGroup() {
            if (!await this.$refs.groupForm?.validate()) return;
            this.savingGroup = true;
            try {
                const response = await api.groupEngagements.updateGroup(this.group.id, {
                    ...(this.group.group_type_allows_custom_name
                        ? { custom_name: this.details.customName.trim() }
                        : {}),
                    coordinator_id: this.details.coordinatorId,
                    notes: this.details.notes.trim() || null,
                    status_code: this.details.statusCode,
                });
                this.applyGroup(response.data);
                this.showGroupDialog = false;
                notify.primary("Group details saved", "top");
            } catch (error) {
                notify.error(errorMessage(error, "Failed to save group details"), "top");
            } finally {
                this.savingGroup = false;
            }
        },
        openNewSession() {
            this.selectedSession = null;
            this.showSessionDialog = true;
        },
        openSession(event, session) {
            if (event?.defaultPrevented) return;
            this.selectedSession = session;
            this.showSessionDialog = true;
        },
        async saveSession(payload) {
            this.savingSession = true;
            try {
                const response = this.selectedSession
                    ? await api.groupEngagements.updateSession(this.group.id, this.selectedSession.id, payload)
                    : await api.groupEngagements.createSession(this.group.id, payload);
                this.applyGroup(response.data);
                this.showSessionDialog = false;
                notify.primary("Group session saved", "top");
            } catch (error) {
                notify.error(errorMessage(error, "Failed to save group session"), "top");
            } finally {
                this.savingSession = false;
            }
        },
    },
};
</script>

<style scoped>
.tk-group-notes {
    margin: 0;
    white-space: pre-wrap;
}

.tk-sessions-table {
    border: 0;
}

.tk-sessions-table :deep(tbody tr) {
    cursor: pointer;
}
</style>
